import { isFeatureEnabled } from './featureFlags';

const TEST_RUN_FLAG = 'ff_journey_e2e_test_mode';
const TEST_RUN_STORAGE_KEY = 'jg:e2e:testRunId';
const TEST_RUN_LOG_PREFIX = 'jg:e2e:logs:';
const MAX_TEST_RUN_LOGS = 400;

function generateTestRunId() {
  const ts = Date.now();
  const rand = Math.random().toString(36).slice(2, 8);
  return `e2e_${ts}_${rand}`;
}

function sanitizeTestRunId(value = '') {
  const raw = String(value || '').trim();
  if (!raw) return '';
  return raw.replace(/[^a-zA-Z0-9:_-]+/g, '').slice(0, 80);
}

export function isJourneyE2ETestModeEnabled() {
  return isFeatureEnabled(TEST_RUN_FLAG, false);
}

export function getCurrentTestRunId() {
  try {
    const stored = localStorage.getItem(TEST_RUN_STORAGE_KEY);
    return sanitizeTestRunId(stored);
  } catch (error) {
    return '';
  }
}

export function getOrCreateTestRunId() {
  if (!isJourneyE2ETestModeEnabled()) return '';
  const existing = getCurrentTestRunId();
  if (existing) return existing;
  const created = generateTestRunId();
  try {
    localStorage.setItem(TEST_RUN_STORAGE_KEY, created);
  } catch (error) {
    // ignore storage failures
  }
  return created;
}

export function clearTestRunId() {
  try {
    localStorage.removeItem(TEST_RUN_STORAGE_KEY);
  } catch (error) {
    // ignore storage failures
  }
}

export function shouldAttachTestRunToUrl(url = '') {
  const normalized = String(url || '').toLowerCase();
  if (!normalized) return false;
  return (
    normalized.includes('/agent/')
    || normalized.includes('/journey-guide')
    || normalized.includes('/track-page-visit')
  );
}

function getLogStorageKey(testRunId = '') {
  const normalized = sanitizeTestRunId(testRunId);
  if (!normalized) return '';
  return `${TEST_RUN_LOG_PREFIX}${normalized}`;
}

export function appendTestRunLog(entry = {}, testRunId = '') {
  const resolvedTestRunId = sanitizeTestRunId(testRunId || getCurrentTestRunId());
  if (!resolvedTestRunId) return false;
  const storageKey = getLogStorageKey(resolvedTestRunId);
  if (!storageKey) return false;

  const nextEntry = {
    ts: new Date().toISOString(),
    ...entry,
    testRunId: resolvedTestRunId
  };

  try {
    const raw = localStorage.getItem(storageKey);
    const current = raw ? JSON.parse(raw) : [];
    const list = Array.isArray(current) ? current : [];
    list.push(nextEntry);
    if (list.length > MAX_TEST_RUN_LOGS) {
      list.splice(0, list.length - MAX_TEST_RUN_LOGS);
    }
    localStorage.setItem(storageKey, JSON.stringify(list));
    return true;
  } catch (error) {
    return false;
  }
}

export function readTestRunLogs(testRunId = '') {
  const resolvedTestRunId = sanitizeTestRunId(testRunId || getCurrentTestRunId());
  const storageKey = getLogStorageKey(resolvedTestRunId);
  if (!storageKey) return [];
  try {
    const raw = localStorage.getItem(storageKey);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
}

export function exportTestRunLogs(testRunId = '') {
  const resolvedTestRunId = sanitizeTestRunId(testRunId || getCurrentTestRunId());
  return {
    testRunId: resolvedTestRunId || null,
    exportedAt: new Date().toISOString(),
    logs: readTestRunLogs(resolvedTestRunId)
  };
}

export function clearTestRunLogs(testRunId = '') {
  const resolvedTestRunId = sanitizeTestRunId(testRunId || getCurrentTestRunId());
  const storageKey = getLogStorageKey(resolvedTestRunId);
  if (!storageKey) return false;
  try {
    localStorage.removeItem(storageKey);
    return true;
  } catch (error) {
    return false;
  }
}

function installWindowHelpers() {
  if (typeof window === 'undefined') return;
  if (window.fsJourneyGuideE2ELogs) return;

  window.fsJourneyGuideE2ELogs = {
    export: (runId = '') => exportTestRunLogs(runId),
    clear: (runId = '') => clearTestRunLogs(runId),
    current: () => getCurrentTestRunId()
  };
}

installWindowHelpers();
